import React from 'react';
import { Row, Col, Typography, Card, Divider } from 'antd';
import {
  SafetyOutlined,
  TeamOutlined,
  DatabaseOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  StarOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;

interface TrustMetric {
  icon: React.ReactNode;
  value: string;
  label: string;
  color: string;
}

const trustMetrics: TrustMetric[] = [
  {
    icon: <TeamOutlined />,
    value: '500+',
    label: 'Active Users',
    color: '#1890ff',
  },
  {
    icon: <DatabaseOutlined />,
    value: '10M+',
    label: 'Data Points Processed',
    color: '#52c41a',
  },
  {
    icon: <StarOutlined />,
    value: '4.8/5',
    label: 'Average Rating',
    color: '#faad14',
  },
  {
    icon: <TrophyOutlined />,
    value: '$1.8M+',
    label: 'Total Savings Generated',
    color: '#722ed1',
  },
];

const certifications = [
  {
    name: 'SOC 2 Compliant',
    description: 'Enterprise-grade security',
  },
  {
    name: 'ISO 27001 Ready',
    description: 'Information security standards',
  },
  {
    name: 'GDPR Compliant',
    description: 'Data privacy protection',
  },
  {
    name: 'Energy Star Partner',
    description: 'Certified energy efficiency',
  },
];

const partnerships = [
  'OpenEI Utility Data',
  'OpenWeatherMap',
  'Google Maps Platform',
  'Stripe Secure Payments',
];

export const TrustBadges: React.FC = () => {
  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <SafetyOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
        <Title level={3}>Trusted by Professionals</Title>
        <Text type="secondary">
          Built by energy engineers with 50+ years combined experience
        </Text>
      </div>

      {/* Trust Metrics */}
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        {trustMetrics.map((metric, index) => (
          <Col xs={12} sm={6} key={index}>
            <Card
              size="small"
              style={{ textAlign: 'center', height: '100%' }}
              hoverable
            >
              <div style={{ fontSize: '32px', color: metric.color, marginBottom: '8px' }}>
                {metric.icon}
              </div>
              <Text strong style={{ fontSize: '24px', display: 'block', marginBottom: '4px' }}>
                {metric.value}
              </Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {metric.label}
              </Text>
            </Card>
          </Col>
        ))}
      </Row>

      <Divider />

      {/* Certifications & Standards */}
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col xs={24} md={12}>
          <Card size="small" title="Certifications & Standards">
            <Row gutter={[16, 16]}>
              {certifications.map((cert, index) => (
                <Col span={24} key={index}>
                  <div style={{ display: 'flex', alignItems: 'start' }}>
                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '12px', marginTop: '4px' }} />
                    <div>
                      <Text strong>{cert.name}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {cert.description}
                      </Text>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card size="small" title="Trusted Partners & Data Sources">
            <Row gutter={[16, 16]}>
              {partnerships.map((partner, index) => (
                <Col span={24} key={index}>
                  <div style={{ display: 'flex', alignItems: 'start' }}>
                    <CheckCircleOutlined style={{ color: '#1890ff', marginRight: '12px', marginTop: '4px' }} />
                    <div>
                      <Text strong>{partner}</Text>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Expert Credentials */}
      <Card
        size="small"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} md={16}>
            <Title level={4} style={{ color: 'white', marginBottom: '8px' }}>
              Built by Energy Experts
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
              Our team includes certified energy engineers, data scientists, and agricultural 
              specialists with decades of combined experience in energy optimization, 
              smart grid technologies, and precision agriculture.
            </Text>
          </Col>
          <Col xs={24} md={8} style={{ textAlign: 'center' }}>
            <div style={{ background: 'rgba(255,255,255,0.2)', padding: '24px', borderRadius: '8px' }}>
              <Text style={{ color: 'white', fontSize: '48px', fontWeight: 'bold', display: 'block' }}>
                50+
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
                Years Combined Experience
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Data Quality Indicators */}
      <Card size="small" style={{ marginTop: '24px' }}>
        <Title level={5} style={{ marginBottom: '16px' }}>
          <DatabaseOutlined style={{ marginRight: '8px' }} />
          Real-Time Data Quality
        </Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <Text strong style={{ fontSize: '20px', color: '#52c41a' }}>99.9%</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>Uptime</Text>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <Text strong style={{ fontSize: '20px', color: '#1890ff' }}>10,000+</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>Active Sensors</Text>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <Text strong style={{ fontSize: '20px', color: '#722ed1' }}>&lt;1s</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>Average Response Time</Text>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default TrustBadges;


